/* eslint-disable */
// @ts-nocheck

import { Search, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useDebounce } from '@/hooks/useDebounce'

interface SearchQueryProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  placeholder?: string
  onlyNumbers?: boolean
}

export const SearchQuery = ({
  searchQuery,
  onSearchChange,
  placeholder = 'Buscar por número de factura...',
  onlyNumbers = false,
}: SearchQueryProps) => {
  const [searchValue, setSearchValue] = useState(searchQuery)
  const isImmediateAction = useRef(false)
  const lastPropValue = useRef(searchQuery)
  const lastDebouncedValue = useRef('')
  const externalClearDetected = useRef(false)

  // Debounce the search value to avoid excessive calls
  const debouncedSearchValue = useDebounce(searchValue, 1000)

  // Sync with external prop changes (like from clear all filters button)
  useEffect(() => {
    // Detect if this is an external clear (prop changed from non-empty to empty)
    const isExternalClear = lastPropValue.current !== '' && searchQuery === ''

    if (lastPropValue.current !== searchQuery && !isImmediateAction.current) {
      if (isExternalClear) {
        // Mark that an external clear was detected
        externalClearDetected.current = true
      }

      setSearchValue(searchQuery)
      lastPropValue.current = searchQuery
      lastDebouncedValue.current = searchQuery
    } else if (isImmediateAction.current) {
      // Reset the immediate action flag
      isImmediateAction.current = false
    }
  }, [searchQuery])

  // Handle debounced search updates (only for user typing, not for clears)
  useEffect(() => {
    // Skip if this is an immediate action or if debounced value hasn't actually changed
    if (
      isImmediateAction.current ||
      lastDebouncedValue.current === debouncedSearchValue
    ) {
      return
    }

    // Skip if an external clear was detected and this debounced value would conflict
    if (externalClearDetected.current && debouncedSearchValue !== searchQuery) {
      // Reset the external clear flag after blocking the conflicting update
      externalClearDetected.current = false
      lastDebouncedValue.current = debouncedSearchValue
      return
    }

    // Only send debounced updates if the debounced value is different from current prop
    if (debouncedSearchValue !== searchQuery) {
      lastPropValue.current = debouncedSearchValue
      lastDebouncedValue.current = debouncedSearchValue
      externalClearDetected.current = false // Reset flag on successful update
      onSearchChange(debouncedSearchValue)
    }
  }, [debouncedSearchValue, searchQuery, onSearchChange])

  // Clear function with immediate action
  const handleClear = useCallback(() => {
    // Mark this as an immediate action to prevent sync conflicts
    isImmediateAction.current = true

    // Update all tracking refs to empty to prevent debounced conflicts
    lastPropValue.current = ''
    lastDebouncedValue.current = ''
    externalClearDetected.current = false

    // Update local state immediately
    setSearchValue('')

    // Update parent immediately (bypass debouncing)
    onSearchChange('')
  }, [onSearchChange])

  // Input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = onlyNumbers
        ? e.target.value.replace(/[^0-9]/g, '')
        : e.target.value

      // Reset flags when user starts typing
      isImmediateAction.current = false
      externalClearDetected.current = false
      setSearchValue(newValue)
    },
    [onlyNumbers]
  )

  return (
    <div className="relative min-w-[240px] flex-1">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        className="bg-white pl-9 pr-4"
        onChange={handleInputChange}
        placeholder={placeholder}
        value={searchValue}
      />
      {searchValue && (
        <Button
          className="absolute right-1 top-1.5 h-7 w-7"
          onClick={handleClear}
          size="icon"
          variant="ghost"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
